/**
 * Slack connection status at the top of the Recipients section
 * (`GET /api/settings/connections`). Connecting happens in the Innu Shell →
 * Connections, never here; the link opens in the top window because this app
 * is an iframe inside innu.app. Static content, so no live region — the SMS
 * banner above already owns `role="status"` for the page.
 */
import { CheckCircle2, ExternalLink, FlaskConical, Info } from 'lucide-react';
import type { ConnectionsStatus, SlackStatus } from './types';

const row = 'flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-[13px] text-gray-900';

export function SlackConnectionBanner({
  connections,
  isError,
  slack,
}: {
  /** `undefined` while loading. */
  connections: ConnectionsStatus | undefined;
  isError: boolean;
  slack: SlackStatus;
}) {
  if (!connections) {
    return (
      <div className="rounded-lg border border-gray-200 bg-page px-3.5 py-2.5 text-[13px] text-muted" data-testid="slack-banner" data-state={isError ? 'error' : 'loading'}>
        {isError
          ? 'Could not check the Slack connection. Slack recipients still save; delivery depends on the connection in Innu.'
          : 'Checking the Slack connection…'}
      </div>
    );
  }

  if (!connections.slack) {
    return (
      <div className={`${row} border-info-blue/30 bg-info-blue-bg`} data-testid="slack-banner" data-state="disconnected">
        <Info size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-info-blue" aria-hidden />
        <div>
          <div className="font-medium">Slack is not connected</div>
          <div className="mt-0.5 text-gray-700">
            Recipients can only be texted until it is.{' '}
            <a
              href={connections.connections_url}
              target="_top"
              rel="noopener"
              className="inline-flex items-center gap-1 font-medium text-info-blue underline underline-offset-2 hover:text-navy"
            >
              Connect Slack in Innu → Connections
              <ExternalLink size={12} strokeWidth={2} aria-hidden />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="slack-banner" data-state={slack.dry_run ? 'connected-dry-run' : 'connected'}>
      <div className={`${row} border-success/30 bg-success-bg`}>
        <CheckCircle2 size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-success" aria-hidden />
        <div>
          <div className="font-medium">Slack is connected</div>
          <div className="mt-0.5 text-gray-700">Recipients on Slack get alerts and nudges in the channel you pick for them.</div>
        </div>
      </div>
      {slack.dry_run && (
        <div className={`${row} border-info-blue/30 bg-info-blue-bg`} data-testid="slack-dry-run">
          <FlaskConical size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-info-blue" aria-hidden />
          <div>
            <div className="font-medium">Dry run: Slack posts are logged, not sent</div>
            <div className="mt-0.5 text-gray-700">
              Every post shows in a candidate's Activity as “Dry run”. Set <code className="mono">SLACK_DRY_RUN</code> to <code className="mono">false</code> on the worker (with the <code className="mono">INNU_CORE_INTERNAL_SECRET</code> secret) to post for real.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
